import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Leaf, Sunrise, Sparkles, CheckCircle, Users, Clock, Award, Calendar } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import PublicHeader from '../components/PublicHeader';

export default function Wellness() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const ayurvedaTreatments = [
    {
      name: 'Panchakarma Therapy',
      icon: '🌿',
      duration: '14-21 days',
      description: 'Ancient detoxification and rejuvenation treatment that cleanses the body and mind',
      benefits: ['Body detoxification', 'Stress relief', 'Improved immunity', 'Mental clarity'],
      price: '$800 - $1,500'
    },
    {
      name: 'Abhyanga Massage',
      icon: '💆',
      duration: '60-90 minutes',
      description: 'Traditional full-body oil massage using warm herbal oils to promote healing',
      benefits: ['Muscle relaxation', 'Better circulation', 'Skin nourishment', 'Pain relief'],
      price: '$40 - $80 per session'
    },
    {
      name: 'Shirodhara',
      icon: '🪔',
      duration: '45-60 minutes',
      description: 'Continuous stream of warm oil poured on forehead for deep relaxation',
      benefits: ['Mental peace', 'Better sleep', 'Reduced anxiety', 'Hormonal balance'],
      price: '$50 - $100 per session'
    },
    {
      name: 'Nasya Treatment',
      icon: '🌺',
      duration: '30-45 minutes',
      description: 'Nasal administration of medicated oils for respiratory and sinus health',
      benefits: ['Sinus relief', 'Improved breathing', 'Mental clarity', 'Reduced headaches'],
      price: '$30 - $60 per session'
    },
    {
      name: 'Ayurvedic Diet Consultation',
      icon: '🥗',
      duration: '60 minutes',
      description: 'Personalized diet plan based on your dosha type for optimal health',
      benefits: ['Balanced nutrition', 'Better digestion', 'Weight management', 'Increased energy'],
      price: '$50 - $100'
    },
    {
      name: 'Herbal Steam Therapy',
      icon: '♨️',
      duration: '30 minutes',
      description: 'Medicated steam therapy to open pores and eliminate toxins',
      benefits: ['Detoxification', 'Skin purification', 'Respiratory health', 'Muscle relaxation'],
      price: '$25 - $50 per session'
    }
  ];

  const yogaPrograms = [
    {
      name: 'Hatha Yoga',
      icon: '🧘',
      level: 'Beginner Friendly',
      duration: '60 minutes',
      description: 'Gentle yoga focusing on physical postures and breathing for post-surgery recovery',
      benefits: ['Flexibility', 'Strength building', 'Balance', 'Stress reduction']
    },
    {
      name: 'Pranayama (Breathing)',
      icon: '🌬️',
      level: 'All Levels',
      duration: '45 minutes',
      description: 'Breathing exercises to enhance lung capacity and promote healing',
      benefits: ['Better oxygen flow', 'Reduced anxiety', 'Improved focus', 'Heart health']
    },
    {
      name: 'Restorative Yoga',
      icon: '🌙',
      level: 'Recovery Focus',
      duration: '75 minutes',
      description: 'Gentle, passive poses held for longer periods for deep healing',
      benefits: ['Deep relaxation', 'Faster recovery', 'Pain management', 'Better sleep']
    },
    {
      name: 'Meditation & Mindfulness',
      icon: '🕉️',
      level: 'All Levels',
      duration: '30-45 minutes',
      description: 'Guided meditation for mental peace and emotional healing',
      benefits: ['Mental clarity', 'Emotional balance', 'Reduced stress', 'Spiritual growth']
    },
    {
      name: 'Yoga Nidra',
      icon: '✨',
      level: 'All Levels',
      duration: '45-60 minutes',
      description: 'Yogic sleep for complete physical, mental, and emotional relaxation',
      benefits: ['Deep rest', 'Nervous system healing', 'Emotional release', 'Better sleep quality']
    },
    {
      name: 'Therapeutic Yoga',
      icon: '💚',
      level: 'Medical Supervision',
      duration: '60 minutes',
      description: 'Customized yoga therapy for specific post-surgical conditions',
      benefits: ['Targeted healing', 'Pain relief', 'Mobility improvement', 'Guided recovery']
    }
  ];

  const wellnessPackages = [
    {
      name: '7-Day Recovery Retreat',
      price: '$1,200',
      duration: '7 days',
      includes: [
        'Daily Ayurveda consultations',
        '2 Ayurveda treatments per day',
        'Daily yoga & meditation sessions',
        'Organic sattvic meals',
        'Accommodation in wellness resort',
        'Progress monitoring'
      ],
      popular: false
    },
    {
      name: '14-Day Healing Program',
      price: '$2,200',
      duration: '14 days',
      includes: [
        'Complete Panchakarma therapy',
        '3 Ayurveda treatments per day',
        'Daily yoga, pranayama & meditation',
        'Personalized diet plan',
        'Luxury wellness accommodation',
        'Follow-up care plan'
      ],
      popular: true
    },
    {
      name: '21-Day Transformation',
      price: '$3,500',
      duration: '21 days',
      includes: [
        'Full Ayurveda assessment',
        'Advanced Panchakarma therapy',
        'Intensive yoga & meditation',
        'Therapeutic massages',
        'Detox & rejuvenation program',
        'Lifetime wellness guidance'
      ],
      popular: false
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Mitchell',
      country: 'USA',
      treatment: 'Post-surgery recovery with Ayurveda & Yoga',
      rating: 5,
      review: 'After my knee replacement surgery in India, I spent 2 weeks at an Ayurveda wellness center. The combination of Panchakarma therapy and gentle yoga accelerated my recovery remarkably. I felt rejuvenated both physically and mentally.',
      date: 'March 2024'
    },
    {
      name: 'David Thompson',
      country: 'UK',
      treatment: '14-Day Healing Program',
      rating: 5,
      review: 'The holistic approach of combining modern surgery with ancient Ayurvedic wisdom is brilliant. The yoga sessions were perfectly tailored to my post-operative condition. Highly recommend this wellness program!',
      date: 'February 2024'
    },
    {
      name: 'Maria Garcia',
      country: 'Spain',
      treatment: 'Panchakarma & Meditation',
      rating: 5,
      review: 'Beyond healing my body after surgery, the Ayurveda treatments and meditation practices healed my mind and spirit. India offers the perfect environment for complete recovery.',
      date: 'January 2024'
    }
  ];

  const handleBookConsultation = () => {
    navigate('/register');
  };

  return (
    <div className="min-h-screen bg-white" data-testid="wellness-page">
      <PublicHeader currentPage="wellness" />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50" data-testid="hero-section">
        <div className="mx-auto max-w-7xl">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-green-100 text-green-800 px-4 py-2 text-base">
              <Leaf className="w-4 h-4 mr-2" />
              Ancient Wisdom for Modern Recovery
            </Badge>
            <h1 className="font-heading text-5xl font-bold text-gray-900 mb-6">
              Post-Recovery <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600">Ayurveda & Yoga</span> Wellness
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-8">
              Complete your healing journey with India's time-tested holistic therapies. Combine modern surgery with ancient Ayurveda and Yoga for faster recovery and lasting wellness.
            </p>
            <div className="flex items-center justify-center gap-4">
              <Button
                size="lg"
                className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-6 text-lg"
                onClick={handleBookConsultation}
                data-testid="hero-cta-btn"
              >
                Start Your Wellness Journey
              </Button>
            </div>
          </div>

          {/* Benefits Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-12">
            {[
              { icon: <Heart className="w-8 h-8" />, title: 'Faster Recovery', desc: 'Accelerate healing naturally' },
              { icon: <Leaf className="w-8 h-8" />, title: 'Natural Therapies', desc: 'Chemical-free treatments' },
              { icon: <Sunrise className="w-8 h-8" />, title: 'Holistic Healing', desc: 'Body, mind & spirit' },
              { icon: <Sparkles className="w-8 h-8" />, title: 'Lasting Wellness', desc: 'Long-term health benefits' }
            ].map((benefit, idx) => (
              <Card key={idx} className="border-2 border-green-200 bg-white/80 backdrop-blur">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center text-white mx-auto mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-sm text-gray-600">{benefit.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Ayurveda Treatments */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="ayurveda-section">
        <div className="mx-auto max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl font-bold text-gray-900 mb-4">
              Ayurveda Treatments for Recovery
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              5,000-year-old healing science personalized for your post-surgical recovery
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ayurvedaTreatments.map((treatment, idx) => (
              <Card key={idx} className="hover:shadow-xl transition-shadow border-2 border-green-100" data-testid={`ayurveda-card-${idx}`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="text-5xl">{treatment.icon}</div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg text-gray-900 mb-1">{treatment.name}</h3>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Clock className="w-4 h-4" />
                        <span>{treatment.duration}</span>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-gray-700 mb-4">{treatment.description}</p>
                  
                  <div className="space-y-2 mb-4">
                    <p className="text-sm font-semibold text-gray-700">Benefits:</p>
                    {treatment.benefits.map((benefit, bidx) => (
                      <div key={bidx} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-600">Starting from</p>
                    <p className="text-lg font-bold text-green-600">{treatment.price}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Yoga Programs */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-green-50 to-emerald-50" data-testid="yoga-section">
        <div className="mx-auto max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl font-bold text-gray-900 mb-4">
              Therapeutic Yoga Programs
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Gentle yoga practices tailored for post-operative healing and recovery
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {yogaPrograms.map((program, idx) => (
              <Card key={idx} className="border-2 border-green-200 bg-white" data-testid={`yoga-card-${idx}`}>
                <CardContent className="p-6">
                  <div className="text-5xl mb-4 text-center">{program.icon}</div>
                  <h3 className="font-semibold text-xl text-gray-900 mb-2 text-center">{program.name}</h3>
                  <Badge variant="outline" className="mb-4 w-full justify-center border-green-300 text-green-700">
                    {program.level}
                  </Badge>
                  
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-600 mb-4">
                    <Clock className="w-4 h-4" />
                    <span>{program.duration}</span>
                  </div>
                  
                  <p className="text-gray-700 text-center mb-4">{program.description}</p>
                  
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-gray-700 text-center">Key Benefits:</p>
                    {program.benefits.map((benefit, bidx) => (
                      <div key={bidx} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Wellness Packages */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="packages-section">
        <div className="mx-auto max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl font-bold text-gray-900 mb-4">
              Complete Wellness Packages
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              All-inclusive recovery programs combining Ayurveda, Yoga, and accommodation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {wellnessPackages.map((pkg, idx) => (
              <Card 
                key={idx} 
                className={`relative ${pkg.popular ? 'border-4 border-green-500 shadow-2xl' : 'border-2 border-green-200'}`}
                data-testid={`package-card-${idx}`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-green-600 text-white px-6 py-2 text-base">
                      <Award className="w-4 h-4 mr-2" />
                      Most Popular
                    </Badge>
                  </div>
                )}
                <CardContent className="p-8">
                  <h3 className="font-heading text-2xl font-bold text-gray-900 mb-2 text-center">{pkg.name}</h3>
                  <div className="text-center mb-6">
                    <p className="text-4xl font-bold text-green-600 mb-1">{pkg.price}</p>
                    <p className="text-gray-600">{pkg.duration}</p>
                  </div>
                  
                  <div className="space-y-3 mb-8">
                    {pkg.includes.map((item, iidx) => (
                      <div key={iidx} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{item}</span>
                      </div>
                    ))}
                  </div>
                  
                  <Button
                    className={`w-full ${pkg.popular ? 'bg-gradient-to-r from-green-600 to-emerald-600' : 'bg-gradient-to-r from-green-500 to-emerald-500'} text-white py-6 text-lg`}
                    onClick={handleBookConsultation}
                  >
                    Book This Package
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-green-50 to-emerald-50" data-testid="testimonials-section">
        <div className="mx-auto max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl font-bold text-gray-900 mb-4">
              Success Stories
            </h2>
            <p className="text-xl text-gray-600">
              Hear from patients who completed their recovery with our wellness programs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, idx) => (
              <Card key={idx} className="border-2 border-green-200" data-testid={`testimonial-${idx}`}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Award key={i} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                    ))}
                  </div>
                  
                  <p className="text-gray-700 mb-4 leading-relaxed italic">"{testimonial.review}"</p>
                  
                  <div className="border-t border-gray-200 pt-4">
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.country}</p>
                    <p className="text-sm text-gray-500 mt-1">{testimonial.treatment}</p>
                    <p className="text-xs text-gray-400 mt-2">{testimonial.date}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-green-600 to-emerald-600 text-white" data-testid="cta-section">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="font-heading text-4xl font-bold mb-6">
            Begin Your Holistic Healing Journey Today
          </h2>
          <p className="text-xl mb-8 opacity-95">
            Combine modern medical treatment with ancient Indian wellness practices for complete recovery
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              size="lg"
              className="bg-white text-green-600 hover:bg-gray-100 font-semibold text-lg px-8 py-6"
              onClick={handleBookConsultation}
              data-testid="cta-consultation-btn"
            >
              Book Consultation
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 font-semibold text-lg px-8 py-6"
              onClick={() => navigate('/treatments')}
              data-testid="explore-treatments-btn"
            >
              Explore Treatments
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
